import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Copy, Building2, MapPin, User, AlertCircle, Briefcase, Cpu, FileText, History, ClipboardList, UserPlus, UserMinus, CheckCircle2, Undo2, ShieldAlert, Send, ExternalLink } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import JobStatusBadge, { JobPriorityBadge } from "@/components/JobStatusBadge";
import MetaCard from "@/components/MetaCard";
import FileUploader from "@/components/FileUploader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { formatDate } from "@/lib/format";
import { formatSlotLabel } from "@/lib/slot";
import { readBack, useWithBack } from "@/lib/nav";
import SubmittedReport from "@/components/SubmittedReport";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("job.write");
  const canAssign = hasPermission("job.assign");
  const canClose = hasPermission("job.close");
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const backCtx = readBack(searchParams);
  const withBackHref = useWithBack();
  const activeTab = searchParams.get("tab") || "overview";
  const setTab = (v) => {
    // Guard against Radix TabsTrigger firing onValueChange twice per click:
    // React state is stale on the second call; window.location.search is not.
    const current = new URLSearchParams(window.location.search).get("tab") || "overview";
    if (v === current) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: false });
  };

  const [job, setJob] = useState(null);
  const [manualsByMachine, setManualsByMachine] = useState({}); // machine_id -> [manuals]
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${id}`);
      setJob(data);
      // pre-fetch merged manuals for each machine
      const results = await Promise.all(
        (data.machines || []).map((m) =>
          api.get(`/machines/${m.id}/manuals`).then((r) => [m.id, r.data.items || []]).catch(() => [m.id, []])
        )
      );
      setManualsByMachine(Object.fromEntries(results));
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/jobs", { replace: true });
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleUnassign = async () => {
    try { await api.post(`/jobs/${id}/unassign`); toast.success("Unassigned"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const handleDeactivate = async () => {
    try { await api.delete(`/jobs/${id}`); toast.success("Deactivated"); navigate("/jobs"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const copyJobNumber = () => {
    if (!job) return;
    navigator.clipboard?.writeText(job.job_number).then(() => toast.success(`${job.job_number} copied`));
  };

  // Phase 6 close/reopen/abort state
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeSkipEmail, setCloseSkipEmail] = useState(false);
  const [closing, setClosing] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [reopening, setReopening] = useState(false);
  const [abortOpen, setAbortOpen] = useState(false);
  const [aborting, setAborting] = useState(false);
  const [retryingEmail, setRetryingEmail] = useState(false);

  const confirmClose = async () => {
    setClosing(true);
    try {
      const { data } = await api.post(`/jobs/${id}/close`, { override_customer_email: closeSkipEmail });
      setJob(data);
      const warnings = data._close_warnings || [];
      toast.success(warnings.length ? `Closed — ${warnings.join("; ")}` : "Job closed");
      setCloseOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setClosing(false); }
  };

  const confirmReopen = async () => {
    setReopening(true);
    try {
      const { data } = await api.post(`/jobs/${id}/reopen`, { reason: reopenReason.trim() });
      setJob(data);
      toast.success("Sent back to engineer");
      setReopenOpen(false); setReopenReason("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReopening(false); }
  };

  const confirmAbort = async () => {
    setAborting(true);
    try {
      await api.post(`/admin/abort-job?id=${id}`);
      toast.success("Job aborted");
      setAbortOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setAborting(false); }
  };

  const retryEmail = async () => {
    setRetryingEmail(true);
    try {
      const { data } = await api.post(`/jobs/${id}/retry-email`, {});
      setJob(data);
      const er = data._email_result || {};
      if (er.sent) toast.success("Email sent");
      else toast.error(er.error || er.skipped || "Email not sent");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRetryingEmail(false); }
  };

  if (loading || !job) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="p-6 space-y-6" data-testid="job-detail-page">
      <Breadcrumbs
        items={[
          { label: "Jobs", to: "/jobs" },
          backCtx
            ? { label: backCtx.label, to: backCtx.to }
            : { label: "All Jobs", to: "/jobs" },
          { label: job.job_number },
        ]}
      />
      <PageHeader
        testId="job-detail-header"
        title={
          <span className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-xl" data-testid="job-detail-number">{job.job_number}</span>
            <span className="text-2xl">— {job.title}</span>
          </span>
        }
        subtitle={
          <span className="flex items-center gap-2 flex-wrap">
            <JobStatusBadge status={job.status} />
            <JobPriorityBadge priority={job.priority} />
            {job.actions_required && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-0.5" data-testid="job-detail-actions-required">
                <AlertCircle className="h-3.5 w-3.5" /> Actions required
              </span>
            )}
            {!job.active && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted border border-border rounded px-2 py-0.5">Deactivated</span>
            )}
          </span>
        }
        actions={<>
          <Button asChild variant="outline" size="sm" data-testid="job-detail-back"><Link to={backCtx?.to || "/jobs"}><ArrowLeft className="h-4 w-4" /> Back</Link></Button>
          {canWrite && ["new","scheduled"].includes(job.status) && <Button asChild size="sm" data-testid="job-detail-edit"><Link to={`/jobs/${id}/edit`}><Pencil className="h-4 w-4" /> Edit</Link></Button>}
          {canAssign && ["new","scheduled"].includes(job.status) && (
            <Button asChild size="sm" variant="outline" data-testid="job-detail-assign"><Link to={`/jobs/${id}/edit#assign`}><UserPlus className="h-4 w-4" /> {job.engineer_id ? "Reassign" : "Assign"}</Link></Button>
          )}
          {canAssign && job.status === "scheduled" && (
            <Button size="sm" variant="outline" onClick={handleUnassign} data-testid="job-detail-unassign"><UserMinus className="h-4 w-4" /> Unassign</Button>
          )}
          {canClose && job.status === "completed" && (
            <>
              <Button size="sm" variant="outline" onClick={() => setReopenOpen(true)} data-testid="job-detail-reopen">
                <Undo2 className="h-4 w-4" /> Send back to engineer
              </Button>
              <Button size="sm" onClick={() => setCloseOpen(true)} data-testid="job-detail-close">
                <CheckCircle2 className="h-4 w-4" /> Close Job
              </Button>
            </>
          )}
          {job.status === "closed" && job.report_pdf_file_id && (
            <Button asChild size="sm" variant="outline" data-testid="job-detail-download-pdf">
              <a href={`/api/jobs/${id}/report.pdf`} target="_blank" rel="noopener noreferrer">
                <FileText className="h-4 w-4" /> Download PDF <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
          {canWrite && job.active && !["in_progress","completed","closed","aborted"].includes(job.status) && (
            <Button size="sm" variant="destructive" onClick={handleDeactivate} data-testid="job-detail-deactivate">Deactivate</Button>
          )}
        </>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Tabs value={activeTab} onValueChange={setTab}>
            <TabsList className="rounded-lg bg-muted p-1">
              <TabsTrigger value="overview" data-testid="job-tab-overview"><Briefcase className="h-3.5 w-3.5 mr-1" /> Overview</TabsTrigger>
              <TabsTrigger value="machines" data-testid="job-tab-machines"><Cpu className="h-3.5 w-3.5 mr-1" /> Machines &amp; History</TabsTrigger>
              {(job.status === "completed" || job.status === "closed") && (
                <TabsTrigger value="report" data-testid="job-tab-report"><FileText className="h-3.5 w-3.5 mr-1" /> Submitted Report</TabsTrigger>
              )}
              <TabsTrigger value="activity" data-testid="job-tab-activity"><History className="h-3.5 w-3.5 mr-1" /> Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-4 space-y-4">
              {job.description && (
                <Card className="rounded-md">
                  <CardHeader><CardTitle className="text-sm font-medium">Description</CardTitle></CardHeader>
                  <CardContent className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="job-detail-description">{job.description}</CardContent>
                </Card>
              )}
              <Card className="rounded-md">
                <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><ClipboardList className="h-4 w-4 text-primary" /> Schedule</CardTitle></CardHeader>
                <CardContent>
                  {job.scheduled_slots?.length ? (
                    <ul className="space-y-2" data-testid="job-detail-schedule">
                      {job.scheduled_slots.map((s, i) => (
                        <li key={i} className="flex items-start justify-between border border-border rounded-sm p-3 bg-card">
                          <div>
                            <div className="text-sm font-medium">{s.date}</div>
                            <div className="text-xs text-muted-foreground">{formatSlotLabel(s)}</div>
                            {s.notes && <div className="text-xs mt-1 whitespace-pre-wrap">{s.notes}</div>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-muted-foreground italic" data-testid="job-detail-schedule-empty">Unscheduled — no slots yet.</div>
                  )}
                </CardContent>
              </Card>
              <Card className="rounded-md">
                <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /> Site access notes</CardTitle></CardHeader>
                <CardContent>
                  {job.site_access_notes_snapshot ? (
                    <>
                      <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="job-detail-access-notes">{job.site_access_notes_snapshot}</div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2">
                        {(job.site?.access_notes || "") === job.site_access_notes_snapshot
                          ? "Source: site-level"
                          : "Source: job-specific override"}
                      </div>
                    </>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">No access notes.</div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="machines" className="mt-4 space-y-4">
              {job.machines?.length === 0 && (
                <Card className="rounded-md"><CardContent className="p-6 text-xs text-muted-foreground italic">No machines on this job.</CardContent></Card>
              )}
              {job.machines?.map((m) => {
                const prevForThis = (job.previous_services || []).filter((p) => (p.machine_ids || []).includes(m.id));
                return (
                  <Card key={m.id} className="rounded-md" data-testid={`job-detail-machine-${m.id}`}>
                    <CardHeader>
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Cpu className="h-4 w-4 text-primary" />
                        <Link to={withBackHref(`/machines/${m.id}`)} className="hover:text-primary" data-testid={`job-detail-machine-link-${m.id}`}>{m.make} {m.model}</Link>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <Field label="Serial" value={<span className="font-mono">{m.serial}</span>} />
                        <Field label="Type" value={m.machine_type?.name || "—"} />
                        <Field label="Install" value={m.install_date || "—"} />
                        <Field label="Status" value={m.active ? "Active" : "Inactive"} />
                      </div>

                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><History className="h-3 w-3" /> Previous services (last 10)</div>
                        {prevForThis.length === 0 ? (
                          <div className="text-xs text-muted-foreground italic" data-testid={`job-detail-prev-empty-${m.id}`}>No previous services on record.</div>
                        ) : (
                          <table className="w-full text-xs border-t border-border">
                            <thead>
                              <tr className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                                <th className="text-left py-1.5 px-2">Job #</th>
                                <th className="text-left py-1.5 px-2">Completed</th>
                                <th className="text-left py-1.5 px-2">Engineer</th>
                                <th className="text-left py-1.5 px-2">Actions?</th>
                                <th className="text-left py-1.5 px-2">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {prevForThis.map((p) => (
                                <tr key={p.id} className="border-t border-border" data-testid={`job-detail-prev-${m.id}-${p.id}`}>
                                  <td className="py-1.5 px-2 font-mono"><Link to={withBackHref(`/jobs/${p.id}`)} className="hover:text-primary">{p.job_number}</Link></td>
                                  <td className="py-1.5 px-2 text-muted-foreground">{p.completed_at ? formatDate(p.completed_at) : "—"}</td>
                                  <td className="py-1.5 px-2 text-muted-foreground">{p.engineer?.full_name || "—"}</td>
                                  <td className="py-1.5 px-2">{p.actions_required ? <AlertCircle className="h-3.5 w-3.5 text-amber-600" /> : <span className="text-muted-foreground">—</span>}</td>
                                  <td className="py-1.5 px-2"><JobStatusBadge status={p.status} /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>

                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Manuals</div>
                        <FileUploader
                          files={manualsByMachine[m.id] || []}
                          readOnly
                          testIdPrefix={`job-detail-manuals-${m.id}`}
                          emptyLabel="No manuals available."
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </TabsContent>

            {(job.status === "completed" || job.status === "closed") && (
              <TabsContent value="report" className="mt-4">
                {job.status === "closed" && (
                  <div className="mb-4 flex items-center justify-between border border-border rounded-sm p-3 bg-muted/40" data-testid="job-report-header">
                    <div className="text-sm">
                      <div className="font-medium">Report closed and archived.</div>
                      <div className="text-xs text-muted-foreground">
                        {job.email_sent_at && <>Emailed to customer {formatDate(job.email_sent_at)}. </>}
                        {job.email_error && <span className="text-destructive">Email issue: {job.email_error}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {job.email_error && (
                        <Button size="sm" variant="outline" onClick={retryEmail} disabled={retryingEmail} data-testid="job-report-retry-email">
                          <Send className="h-3.5 w-3.5" /> {retryingEmail ? "Retrying…" : "Retry email"}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <SubmittedReport job={job} />
              </TabsContent>
            )}

            <TabsContent value="activity" className="mt-4">
              <Card className="rounded-md">
                <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><History className="h-4 w-4 text-primary" /> Activity log</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {(job.activity_log || []).length === 0 ? (
                    <div className="p-6 text-xs text-muted-foreground italic">No activity yet.</div>
                  ) : (
                    <ol className="divide-y divide-border">
                      {[...(job.activity_log || [])].reverse().map((a, i) => (
                        <li key={i} className="p-3 flex items-start justify-between gap-3" data-testid={`job-activity-${i}`}>
                          <div>
                            <div className="text-sm font-medium">{a.message}</div>
                            <div className="text-xs text-muted-foreground">
                              <span className="uppercase tracking-wider font-bold text-[10px] mr-2">{a.kind}</span>
                              by {a.actor_username}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground shrink-0">{formatDate(a.ts)}</div>
                        </li>
                      ))}
                    </ol>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right meta column */}
        <div className="space-y-4">
          <MetaCard
            title="Assignment"
            fields={[
              {
                label: "Customer",
                value: job.customer ? (
                  <Link to={withBackHref(`/customers/${job.customer.id}`)} className="inline-flex items-center gap-1 hover:text-primary" data-testid="job-detail-customer">
                    <Building2 className="h-3 w-3" /> {job.customer.name}
                  </Link>
                ) : "—",
              },
              {
                label: "Site",
                value: job.site ? (
                  <Link to={withBackHref(`/sites/${job.site.id}`)} className="inline-flex items-center gap-1 hover:text-primary" data-testid="job-detail-site">
                    <MapPin className="h-3 w-3" /> {job.site.name}
                  </Link>
                ) : "—",
              },
              {
                label: "Engineer",
                value: job.engineer ? (
                  <span className="inline-flex items-center gap-1" data-testid="job-detail-engineer"><User className="h-3 w-3" /> {job.engineer.full_name}</span>
                ) : <span className="italic text-muted-foreground">Unassigned</span>,
              },
              { label: "Machines", value: job.machines?.length || 0 },
              { label: "Priority", value: <JobPriorityBadge priority={job.priority} /> },
              { label: "Duration", value: { half_day: "Half Day", all_day: "All Day", custom: "Custom" }[job.duration_estimate] || job.duration_estimate },
            ]}
          />
          <MetaCard
            title="Metadata"
            fields={[
              {
                label: "Job #",
                value: (
                  <span className="inline-flex items-center gap-2 font-mono">
                    {job.job_number}
                    <button type="button" onClick={copyJobNumber} className="text-muted-foreground hover:text-foreground" data-testid="job-detail-copy-number"><Copy className="h-3 w-3" /></button>
                  </span>
                ),
              },
              { label: "Status", value: <JobStatusBadge status={job.status} /> },
              { label: "Created", value: formatDate(job.created_at) },
              { label: "Updated", value: formatDate(job.updated_at) },
              ...(job.completed_at ? [{ label: "Completed", value: formatDate(job.completed_at) }] : []),
              ...(job.closed_at ? [{ label: "Closed", value: formatDate(job.closed_at) }] : []),
            ]}
          />
          {isSuperAdmin && job.status === "in_progress" && (
            <Card className="rounded-md border-destructive/40" data-testid="job-detail-advanced">
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive"><ShieldAlert className="h-4 w-4" /> Advanced</CardTitle>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground space-y-2">
                <div>Force-abort a stuck in_progress job. This bypasses the engineer flow and marks the job <span className="font-mono">aborted</span>.</div>
                <Button size="sm" variant="destructive" onClick={() => setAbortOpen(true)} data-testid="job-detail-force-abort">
                  <ShieldAlert className="h-3.5 w-3.5" /> Force abort
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Close Job dialog */}
      <Dialog open={closeOpen} onOpenChange={setCloseOpen}>
        <DialogContent className="max-w-lg" data-testid="close-job-dialog">
          <DialogHeader>
            <DialogTitle>Close job {job.job_number}?</DialogTitle>
            <DialogDescription>
              This generates the PDF report{job.customer?.email_reports_default ? " and emails it to the customer." : "."}
              {" "}Job status becomes <span className="font-mono">closed</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <div className="border border-border rounded-sm p-3 bg-muted/40 text-xs space-y-1">
              <div><span className="uppercase tracking-wider text-muted-foreground text-[10px] font-bold">Customer</span> · {job.customer?.name || job.customer_name}</div>
              <div><span className="uppercase tracking-wider text-muted-foreground text-[10px] font-bold">Contact</span> · {(job.customer?.contacts?.find?.(c => c.is_report_recipient)?.email) || job.customer?.contacts?.[0]?.email || "no email on file"}</div>
              <div><span className="uppercase tracking-wider text-muted-foreground text-[10px] font-bold">Email reports</span> · {job.customer?.email_reports_default ? "yes (default on)" : "off"}</div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={closeSkipEmail} onCheckedChange={(v) => setCloseSkipEmail(!!v)} data-testid="close-job-skip-email" />
              <span className="text-sm">Skip customer email for this job</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseOpen(false)} data-testid="close-job-cancel">Cancel</Button>
            <Button asChild variant="outline" data-testid="close-job-preview-pdf">
              <a
                href={`/api/jobs/${id}/report.pdf?preview=true`}
                target="_blank" rel="noopener noreferrer"
              >
                <FileText className="h-4 w-4" /> Preview PDF
              </a>
            </Button>
            <Button onClick={confirmClose} disabled={closing} data-testid="close-job-confirm">
              {closing ? "Closing…" : "Confirm close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send back dialog */}
      <Dialog open={reopenOpen} onOpenChange={setReopenOpen}>
        <DialogContent className="max-w-lg" data-testid="reopen-job-dialog">
          <DialogHeader>
            <DialogTitle>Send {job.job_number} back to engineer?</DialogTitle>
            <DialogDescription>Status returns to <span className="font-mono">in_progress</span>. The engineer will see your reason in the activity log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label className="text-xs">Reason <span className="text-destructive">*</span></Label>
            <Textarea value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={4} placeholder="What needs to be redone or clarified?" data-testid="reopen-job-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReopenOpen(false)} data-testid="reopen-job-cancel">Cancel</Button>
            <Button variant="destructive" onClick={confirmReopen} disabled={reopening || reopenReason.trim().length < 5} data-testid="reopen-job-confirm">
              {reopening ? "Reopening…" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Force abort dialog */}
      <Dialog open={abortOpen} onOpenChange={setAbortOpen}>
        <DialogContent className="max-w-lg" data-testid="abort-job-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Force abort {job.job_number}?</DialogTitle>
            <DialogDescription>
              Only use this if a job is genuinely stuck. Status becomes <span className="font-mono">aborted</span> and can then be purged. This bypasses the normal engineer completion flow.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbortOpen(false)} data-testid="abort-job-cancel">Cancel</Button>
            <Button variant="destructive" onClick={confirmAbort} disabled={aborting} data-testid="abort-job-confirm">
              {aborting ? "Aborting…" : "Yes, force abort"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm mt-0.5">{value ?? "—"}</div>
    </div>
  );
}
